import React, { useState, useEffect } from "react";
import Moment from "react-moment";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";

import { SubTab } from "../Cms/TabNInput";
import useRequest from "../../hooks/useRequest";
import Breadcrumb from "../Breadcrumb/Breadcrumb";
import { API } from "../../constant/api";
import { SlideshowLightbox } from "lightbox.js-react";
import "lightbox.js-react/dist/index.css";
import SendOffer from "./SendOffer";
import InvoiceOrder from "./InvoiceOrder";
import { ConfirmButton } from "../sweetalert";
import { requestHandler } from "../../services/requestHandler";

const statusArrayForInvoice = [
  "Order Confirmed",
  "Order Delivered",
  "Order Partially Returned",
  "Order Returned",
];

const lockedStatuses = [
  "Order Confirmed",
  "Order Canelled",
  "Order Cancelled",
  "Order Delivered",
  "Order Returned",
  "Order Partially Returned",
];

const getStatusBadgeClass = (status) => {
  if (
    status === "Order Confirmed" ||
    status === "Request Submitted" ||
    status === "Order Delivered"
  ) {
    return "success";
  }
  if (status === "Offer Received") {
    return "warning";
  }
  if (
    status === "Order Returned" ||
    status === "Order Partially Returned" ||
    status === "Order Cancelled" ||
    status === "Order Canelled"
  ) {
    return "danger";
  }
  return "primary";
};

const ViewOne = (props) => {
  const { id } = props.match.params;
  const dispatch = useDispatch();

  const [tabMenu, setTabMenu] = useState([
    "Order Information",
    "Items",
    "Vendor Offers",
  ]);
  const [userData, setUserData] = useState([]);
  const [userDetails, setUserDetails] = useState([]);
  const [orderOffer, setOrderOffer] = useState([]);
  const [isShowForm, setIsShowForm] = useState(false);
  const [orderStatus, setOrderStatus] = useState("");
  const [tabIndex, setTabIndex] = useState(0);

  const { response: responseUserData, request: requestUserData } = useRequest();
  const { response: responseItemData, request: requestItemData } = useRequest();
  const { response: responseVendorOfferData, request: requestVendorOfferData } =
    useRequest();

  const { date_time_format } = useSelector((state) => state.setting);

  useEffect(() => {
    requestUserData("GET", `orders/${id}`);
    document.title = "View Order - Easyspare";
  }, []);

  useEffect(() => {
    requestItemData("GET", `orders/item/${id}`);
  }, []);

  useEffect(() => {
    requestVendorOfferData("GET", `orders/offer/new/${id}`);
  }, [isShowForm]);

  useEffect(() => {
    setOrderOffer(responseVendorOfferData?.orderOffer || []);
  }, [responseVendorOfferData]);

  useEffect(() => {
    if (responseUserData?.orders) {
      const {
        requestId,
        brandName,
        customer,
        contact,
        modelName,
        year,
        sparePartsLength,
        vin,
        brandImage,
        status,
        createdAt,
      } = responseUserData.orders;

      setOrderStatus(status || "");

      if (statusArrayForInvoice.includes(status)) {
        setTabMenu((prev) =>
          prev.includes("Order Invoice") ? prev : [...prev, "Order Invoice"]
        );
      }

      setUserData([
        { title: "Order Number", value: requestId || "-", type: "text" },
        { title: "Brand Name", value: brandName || "-", type: "text" },
        {
          title: "Brand Image",
          value: brandImage?.length > 0 ? brandImage : null,
          type: "image",
        },
        {
          title: "Customer Info",
          value: `${customer || "-"}${contact ? ` , ${contact}` : ""}`,
          type: "text",
        },
        { title: "Model Name", value: modelName || "-", type: "text" },
        { title: "Manufacture Year", value: year || "-", type: "text" },
        { title: "Vin", value: vin || "-", type: "text" },
        { title: "Total Items", value: sparePartsLength ?? "-", type: "text" },
        { title: "Status", value: status || "-", type: "status" },
        {
          title: "Created On",
          value: createdAt ? (
            <Moment format={date_time_format}>{createdAt}</Moment>
          ) : (
            "-"
          ),
          type: "text",
        },
      ]);
    }
  }, [responseUserData, date_time_format]);

  useEffect(() => {
    if (responseItemData) {
      setUserDetails(responseItemData?.orderOffer || []);
    }
  }, [responseItemData]);

  const currentTabIndexHandler = (idx) => {
    setTabIndex(idx);
    if (idx !== 2) {
      setIsShowForm(false);
    }
  };

  const deleteHandler = async () => {
    const swal = await ConfirmButton(
      "Yes, delete it",
      "Are you sure you want to permanently delete this order? This cannot be undone."
    );
    if (!swal.isConfirmed) return;

    const response = await requestHandler(
      "DELETE",
      "orders",
      { id },
      dispatch
    );

    if (response?.status) {
      toast.success(response.message);
      props.history.push("/orders");
    }
  };

  const canSendOffer = orderStatus && !lockedStatuses.includes(orderStatus);

  const renderFieldValue = (field) => {
    if (field.type === "image") {
      if (!field.value) {
        return <span className="text-muted">No pictures found</span>;
      }
      return (
        <img data-fancybox src={field.value} width="100px" alt="Brand" />
      );
    }

    if (field.type === "status") {
      return (
        <span
          className={`label label-lg label-light-${getStatusBadgeClass(
            field.value
          )} label-inline`}
        >
          {field.value}
        </span>
      );
    }

    return (
      <span className="form-control-plaintext font-weight-bolder">
        {field.value}
      </span>
    );
  };

  return (
    <div
      className="content d-flex flex-column flex-column-fluid"
      id="kt_content"
    >
      <Breadcrumb
        title="View Order"
        links={[
          { to: "/", name: "Dashboard" },
          { to: "/orders", name: "Back To Orders" },
        ]}
      />
      <div className="d-flex flex-column-fluid">
        <div className="container">
          <div className="card card-custom gutter-b">
            <div className="card-header card-header-tabs-line">
              <div className="card-toolbar d-flex justify-content-between w-100 align-items-center flex-wrap">
                <ul
                  className="nav nav-tabs nav-tabs-space-lg nav-tabs-line nav-bold nav-tabs-line-3x mb-0"
                  role="tablist"
                >
                  {tabMenu.map((data, index) => (
                    <SubTab
                      key={index}
                      name={data}
                      index={index}
                      onClick={currentTabIndexHandler}
                      currentIndex={tabIndex}
                    />
                  ))}
                </ul>
                <div className="d-flex align-items-center mt-2 mt-md-0">
                  {canSendOffer && tabIndex === 2 ? (
                    <button
                      type="button"
                      className="btn btn-sm btn-primary mr-2"
                      onClick={() => setIsShowForm(!isShowForm)}
                    >
                      {isShowForm ? "Show Offers" : "Send Offer"}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    onClick={deleteHandler}
                  >
                    Delete Order
                  </button>
                </div>
              </div>
            </div>

            {/* Order Information */}
            <div
              style={{ display: tabIndex === 0 ? "block" : "none" }}
              className="card-body"
            >
              {userData.length === 0 ? (
                <p className="text-muted mb-0">Loading order details...</p>
              ) : (
                userData.map((field, index) => (
                  <div key={index} className="form-group row my-2">
                    <label className="col-4 col-form-label font-weight-bold">
                      {field.title}
                    </label>
                    <div className="col-8">{renderFieldValue(field)}</div>
                  </div>
                ))
              )}
            </div>

            {/* Items */}
            <div
              style={{ display: tabIndex === 1 ? "block" : "none" }}
              className="card-body"
            >
              {userDetails.length === 0 ? (
                <p className="text-muted mb-0">No items found for this order.</p>
              ) : (
                <div className="accordion" id="accordionItems">
                  {userDetails.map((item, index) => {
                    const collapseId = `item-collapse-${index}`;
                    return (
                      <div className="card mb-3" key={item?._id || index}>
                        <div className="card-header" id={`item-heading-${index}`}>
                          <button
                            className="btn btn-link rowInBtn text-left w-100"
                            type="button"
                            data-toggle="collapse"
                            data-target={`#${collapseId}`}
                            aria-expanded="true"
                            aria-controls={collapseId}
                          >
                            <span className="partsHead">
                              <span>Name of Part</span>
                              {item?.spareParts?.name || "-"}
                            </span>
                            <span className="partsHead">
                              <span>Part Number</span>
                              {item?.spareParts?.partNumber || "-"}
                            </span>
                          </button>
                        </div>
                        <div
                          id={collapseId}
                          className="collapse show"
                          aria-labelledby={`item-heading-${index}`}
                          data-parent="#accordionItems"
                        >
                          <div className="card-body">
                            <div className="mb-4">
                              <span className="partInfoLabel">Quantity: </span>
                              {item?.spareParts?.quantity || "-"}
                            </div>
                            <div className="mb-4">
                              <span className="partInfoLabel">Description: </span>
                              {item?.spareParts?.description || "-"}
                            </div>
                            <span className="partInfoLabel">Images: </span>
                            <div className="row mt-2">
                              {item?.spareParts?.images?.length > 0 ? (
                                <div className="partsImageCard col-12">
                                  <SlideshowLightbox>
                                    {item.spareParts.images.map((image, i) => (
                                      <img
                                        key={i}
                                        src={`${API.PORT}/${image}`}
                                        alt="part"
                                        style={{
                                          width: "120px",
                                          height: "90px",
                                          cursor: "pointer",
                                          marginRight: "10px",
                                          objectFit: "cover",
                                        }}
                                      />
                                    ))}
                                  </SlideshowLightbox>
                                </div>
                              ) : (
                                <span className="text-muted ml-3">No images</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Vendor Offers */}
            <div
              style={{ display: tabIndex === 2 ? "block" : "none" }}
              className="card-body"
            >
              {isShowForm ? (
                <SendOffer id={id} setTabIndex={setTabIndex} />
              ) : orderOffer.length === 0 ? (
                <p className="text-muted mb-0">
                  No vendor offers yet.
                  {canSendOffer
                    ? " Use Send Offer to create one."
                    : ""}
                </p>
              ) : (
                <div className="accordion" id="accordionVendorOffers">
                  {orderOffer.map((item, index) => {
                    const collapseId = `vendor-collapse-${index}`;
                    return (
                      <div className="card mb-3" key={item?._id || index}>
                        <div
                          className="card-header"
                          id={`vendor-heading-${index}`}
                        >
                          <button
                            className="btn btn-link rowInBtn text-left w-100"
                            type="button"
                            data-toggle="collapse"
                            data-target={`#${collapseId}`}
                            aria-expanded="true"
                            aria-controls={collapseId}
                          >
                            <span className="partsHead">
                              <span>Name of Vendor</span>
                              {`${item.vendorsData?.firstName || ""} ${
                                item.vendorsData?.lastName || ""
                              }`.trim() || "-"}
                            </span>
                            <span className="partsHead">
                              <span>Email</span>
                              {item.vendorsData?.email || "N/A"}
                            </span>
                          </button>
                        </div>
                        <div
                          id={collapseId}
                          className="collapse show"
                          aria-labelledby={`vendor-heading-${index}`}
                          data-parent="#accordionVendorOffers"
                        >
                          <div className="card-body">
                            {item.items?.length > 0
                              ? item.items.map((val, itemIndex) => (
                                  <div
                                    className="vendoritemList mb-4 pb-4 border-bottom"
                                    key={`${itemIndex}-${val.sparePart?.partNumber}`}
                                  >
                                    <div className="mb-3">
                                      <span className="partInfoLabel">
                                        Name of Part:{" "}
                                      </span>
                                      {val.sparePart?.partName || "N/A"}
                                    </div>
                                    <div className="mb-3">
                                      <span className="partInfoLabel">
                                        Price:{" "}
                                      </span>
                                      {val.ordersOffersData?.price || 0}
                                    </div>
                                    <div className="mb-3">
                                      <span className="partInfoLabel">
                                        Is Part Available:{" "}
                                      </span>
                                      {val.ordersOffersData?.isPartAvailable
                                        ? "YES"
                                        : "NO"}
                                    </div>
                                    <div className="mb-3">
                                      <span className="partInfoLabel">
                                        Is Part Genuine:{" "}
                                      </span>
                                      {val.ordersOffersData?.isPartGenuine
                                        ? "YES"
                                        : "NO"}
                                    </div>
                                    <div className="mb-3">
                                      <span className="partInfoLabel">
                                        Description:{" "}
                                      </span>
                                      {val.sparePart?.description || "N/A"}
                                    </div>
                                    <div className="mb-3">
                                      <span className="partInfoLabel">
                                        Notes:{" "}
                                      </span>
                                      {val.ordersOffersData?.notes || "N/A"}
                                    </div>
                                    <div className="mb-3">
                                      <span className="partInfoLabel">
                                        Shipment Notes:{" "}
                                      </span>
                                      {val.ordersOffersData?.shipmentnotes ||
                                        "N/A"}
                                    </div>
                                    <div className="mb-3">
                                      <span className="partInfoLabel">
                                        Images:{" "}
                                      </span>
                                      <div className="row mt-2">
                                        {val.ordersOffersData?.images?.length >
                                        0 ? (
                                          <div className="partsImageCard col-12">
                                            <SlideshowLightbox>
                                              {val.ordersOffersData.images.map(
                                                (image, i) => (
                                                  <img
                                                    key={i}
                                                    src={`${API.PORT}/${image}`}
                                                    alt="offer"
                                                    style={{
                                                      width: "120px",
                                                      height: "90px",
                                                      cursor: "pointer",
                                                      marginRight: "10px",
                                                      objectFit: "cover",
                                                    }}
                                                  />
                                                )
                                              )}
                                            </SlideshowLightbox>
                                          </div>
                                        ) : (
                                          <span className="text-muted ml-3">
                                            No images
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))
                              : null}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {tabIndex === 3 ? <InvoiceOrder orderId={id} /> : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewOne;
